import Custom404 from "@dust-tt/front/components/pages/Custom404";
import { AnalyticsPage } from "@dust-tt/front/components/poke/pages/AnalyticsPage";
import { AppPage } from "@dust-tt/front/components/poke/pages/AppPage";
import { AssistantDetailsPage } from "@dust-tt/front/components/poke/pages/AssistantDetailsPage";
import { AssistantInstructionsPage } from "@dust-tt/front/components/poke/pages/AssistantInstructionsPage";
import { CacheLookupPage } from "@dust-tt/front/components/poke/pages/CacheLookupPage";
import { ConnectorRedirectPage } from "@dust-tt/front/components/poke/pages/ConnectorRedirectPage";
import { ConversationPage } from "@dust-tt/front/components/poke/pages/ConversationPage";
import { CouponsPage } from "@dust-tt/front/components/poke/pages/CouponsPage";
import { DashboardPage } from "@dust-tt/front/components/poke/pages/DashboardPage";
import { DataSourcePage } from "@dust-tt/front/components/poke/pages/DataSourcePage";
import { DataSourceQueryPage } from "@dust-tt/front/components/poke/pages/DataSourceQueryPage";
import { DataSourceSearchPage } from "@dust-tt/front/components/poke/pages/DataSourceSearchPage";
import { DataSourceViewPage } from "@dust-tt/front/components/poke/pages/DataSourceViewPage";
import { EmailTemplatesPage } from "@dust-tt/front/components/poke/pages/EmailTemplatesPage";
import { FeatureFlagDetailPage } from "@dust-tt/front/components/poke/pages/FeatureFlagDetailPage";
import { FeatureFlagsPage } from "@dust-tt/front/components/poke/pages/FeatureFlagsPage";
import { FramePage } from "@dust-tt/front/components/poke/pages/FramePage";
import { GlobalAgentFeedbacksPage } from "@dust-tt/front/components/poke/pages/GlobalAgentFeedbacksPage";
import { GroupPage } from "@dust-tt/front/components/poke/pages/GroupPage";
import { KillPage } from "@dust-tt/front/components/poke/pages/KillPage";
import { LLMTracePage } from "@dust-tt/front/components/poke/pages/LLMTracePage";
import { MCPServerViewPage } from "@dust-tt/front/components/poke/pages/MCPServerViewPage";
import { MembershipsPage } from "@dust-tt/front/components/poke/pages/MembershipsPage";
import { NotionRequestsPage } from "@dust-tt/front/components/poke/pages/NotionRequestsPage";
import { PlansPage } from "@dust-tt/front/components/poke/pages/PlansPage";
import { PluginsPage } from "@dust-tt/front/components/poke/pages/PluginsPage";
import { PodFunctionPage } from "@dust-tt/front/components/poke/pages/PodFunctionPage";
import { PokefyPage } from "@dust-tt/front/components/poke/pages/PokefyPage";
import { ProductionChecksPage } from "@dust-tt/front/components/poke/pages/ProductionChecksPage";
import { SkillDetailsPage } from "@dust-tt/front/components/poke/pages/SkillDetailsPage";
import { SkillSuggestionDetailsPage } from "@dust-tt/front/components/poke/pages/SkillSuggestionDetailsPage";
import { SpaceDataSourceViewPage } from "@dust-tt/front/components/poke/pages/SpaceDataSourceViewPage";
import { SpacePage } from "@dust-tt/front/components/poke/pages/SpacePage";
import { TemplateDetailPage } from "@dust-tt/front/components/poke/pages/TemplateDetailPage";
import { TemplatesListPage } from "@dust-tt/front/components/poke/pages/TemplatesListPage";
import { TriggerDetailsPage } from "@dust-tt/front/components/poke/pages/TriggerDetailsPage";
import { WebhookSourceDetailsPage } from "@dust-tt/front/components/poke/pages/WebhookSourceDetailsPage";
import { WorkspacePage } from "@dust-tt/front/components/poke/pages/WorkspacePage";
import { GlobalErrorFallback } from "@spa/app/components/GlobalErrorFallback";
import { RootRouterLayout } from "@spa/app/layouts/RootRouterLayout";
import { UnauthenticatedPage } from "@spa/app/layouts/UnauthenticatedPage";
import { PokePage } from "@spa/poke/layouts/PokePage";
import { PokeWorkspacePage } from "@spa/poke/layouts/PokeWorkspacePage";
import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation, useParams } from "react-router-dom";

// Redirect component that strips /poke prefix
function PokeRedirect() {
  const params = useParams();
  const location = useLocation();
  const rest = params["*"] || "";
  return <Navigate to={`/${rest}${location.search}${location.hash}`} replace />;
}

// Redirect app-style builder/agents/:aId to poke-style assistants/:aId.
function BuilderAgentRedirect() {
  const { aId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`../assistants/${aId}${location.search}${location.hash}`}
      replace
    />
  );
}

export const routes: RouteObject[] = [
  {
    element: <RootRouterLayout />,
    errorElement: <GlobalErrorFallback />,
    children: [
      {
        path: "/",
        element: <PokePage />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "kill", element: <KillPage /> },
          { path: "plans", element: <PlansPage /> },
          { path: "coupons", element: <CouponsPage /> },
          { path: "pokefy", element: <PokefyPage /> },
          { path: "feature-flags", element: <FeatureFlagsPage /> },
          {
            path: "feature-flags/:flagName",
            element: <FeatureFlagDetailPage />,
          },
          { path: "production-checks", element: <ProductionChecksPage /> },
          { path: "email-templates", element: <EmailTemplatesPage /> },
          {
            path: "global-agent-feedbacks",
            element: <GlobalAgentFeedbacksPage />,
          },
          { path: "templates", element: <TemplatesListPage /> },
          { path: "templates/:tId", element: <TemplateDetailPage /> },
          { path: "plugins", element: <PluginsPage /> },
          { path: "cache", element: <CacheLookupPage /> },
          {
            path: "connectors/:connectorId",
            element: <ConnectorRedirectPage />,
          },
        ],
      },
      {
        path: "/:wId",
        element: <PokeWorkspacePage />,
        children: [
          { index: true, element: <WorkspacePage /> },
          { path: "analytics", element: <AnalyticsPage /> },
          { path: "memberships", element: <MembershipsPage /> },
          { path: "llm-traces/:runId", element: <LLMTracePage /> },
          {
            path: "assistants/:aId/instructions",
            element: <AssistantInstructionsPage />,
          },
          { path: "assistants/:aId", element: <AssistantDetailsPage /> },
          {
            path: "assistants/:aId/triggers/:triggerId",
            element: <TriggerDetailsPage />,
          },
          { path: "conversation/:cId", element: <ConversationPage /> },
          { path: "data_sources/:dsId", element: <DataSourcePage /> },
          {
            path: "data_sources/:dsId/notion-requests",
            element: <NotionRequestsPage />,
          },
          {
            path: "data_sources/:dsId/query",
            element: <DataSourceQueryPage />,
          },
          {
            path: "data_sources/:dsId/search",
            element: <DataSourceSearchPage />,
          },
          {
            path: "data_sources/:dsId/view",
            element: <DataSourceViewPage />,
          },
          {
            path: "connectors/:connectorId",
            element: <ConnectorRedirectPage />,
          },
          { path: "groups/:groupId", element: <GroupPage /> },
          { path: "files/:sId", element: <FramePage /> },
          { path: "skills/:sId", element: <SkillDetailsPage /> },
          {
            path: "suggestions/:suggestionId",
            element: <SkillSuggestionDetailsPage />,
          },
          { path: "spaces/:spaceId", element: <SpacePage /> },
          { path: "spaces/:spaceId/apps/:appId", element: <AppPage /> },
          {
            path: "spaces/:spaceId/data_source_views/:dsvId",
            element: <SpaceDataSourceViewPage />,
          },
          {
            path: "spaces/:spaceId/mcp_server_views/:svId",
            element: <MCPServerViewPage />,
          },
          {
            path: "spaces/:spaceId/pod_functions/:functionId",
            element: <PodFunctionPage />,
          },
          {
            path: "webhook-sources/:wsId",
            element: <WebhookSourceDetailsPage />,
          },
          // Redirect app-style URLs to poke-style URLs.
          {
            path: "builder/agents/:aId",
            element: <BuilderAgentRedirect />,
          },
        ],
      },
      // Redirect /poke/* to /* (strip /poke prefix)
      { path: "poke/*", element: <PokeRedirect /> },
      { path: "w/*", element: <PokeRedirect /> },
      {
        element: <UnauthenticatedPage />,
        children: [{ path: "*", element: <Custom404 /> }],
      },
    ],
  },
];
